import { Clock, Link2, Target, Zap } from "lucide-react";
import { FunctionComponent } from "react";
import {
  Exercise,
  ExerciseSet,
  Workout,
} from "@/components/workouts/workoutsColumns";
import { sectionTypeIcons } from "@/components/workouts/WorkoutSectionHeader";
import {
  WORKOUT_SECTION_LABELS,
  isLooseSection,
  isWorkoutSectionType,
} from "@/lib/utils/workoutSections";
import {
  groupSessionBlocks,
  hasCustomSectionName,
  sessionSectionLabel,
} from "@/lib/utils/workoutSectionClock";
import { sessionBlockKey } from "@/lib/utils/loggerReorder";
import { cn } from "@/lib/utils/ui";

type WorkoutImportPreviewProps = {
  workout: Workout;
  compact?: boolean;
  getSetTargetLabel: (
    trackingType: string | null,
    set: ExerciseSet,
  ) => string;
};

const BadgeWithTitle: FunctionComponent<{
  title: string;
  value: string;
  icon?: React.ReactNode;
}> = ({ title, value, icon }) => (
  <div className="flex items-center gap-2 rounded-lg border border-border/50 bg-muted/50 px-3 py-2">
    {icon}
    <div className="flex flex-col">
      <span className="text-xs font-medium text-muted-foreground">{title}</span>
      <span className="text-sm font-semibold text-foreground">{value}</span>
    </div>
  </div>
);

const ExercisePreview = ({
  exercise,
  compact,
  getSetTargetLabel,
}: {
  exercise: Exercise;
  compact?: boolean;
  getSetTargetLabel: WorkoutImportPreviewProps["getSetTargetLabel"];
}) => (
  <div className="space-y-3">
    <h3 className="text-base font-semibold text-foreground sm:text-lg">
      {exercise.name}
    </h3>
    <div className={cn("flex flex-wrap gap-2", compact ? "" : "gap-3")}>
      {exercise.tempo ? (
        <BadgeWithTitle
          title="Tempo"
          value={exercise.tempo}
          icon={<Clock className="h-3 w-3" />}
        />
      ) : null}
      {exercise.rest_time != null ? (
        <BadgeWithTitle
          title="Rest"
          value={`${exercise.rest_time}s`}
          icon={<Zap className="h-3 w-3" />}
        />
      ) : null}
      {exercise.rir != null ? (
        <BadgeWithTitle
          title="RIR"
          value={exercise.rir.toString()}
          icon={<Target className="h-3 w-3" />}
        />
      ) : null}
    </div>
    {exercise.sets && exercise.sets.length > 0 ? (
      <div className="space-y-2">
        <h4 className="text-sm font-medium text-muted-foreground">Sets</h4>
        <div
          className={cn(
            "grid gap-2",
            compact
              ? "grid-cols-1 sm:grid-cols-2"
              : "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4",
          )}
        >
          {exercise.sets.map((set, setIndex) => (
            <div
              key={set.id ?? setIndex}
              className="flex items-center gap-2 rounded-lg bg-muted/50 p-2"
            >
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/20 text-xs font-semibold text-primary">
                {setIndex + 1}
              </span>
              <span className="text-sm text-foreground">
                {getSetTargetLabel(exercise.tracking_type, set)}
              </span>
            </div>
          ))}
        </div>
      </div>
    ) : null}
  </div>
);

const GroupPreview = ({
  group,
  compact,
  getSetTargetLabel,
}: {
  group: Exercise[];
  compact?: boolean;
  getSetTargetLabel: WorkoutImportPreviewProps["getSetTargetLabel"];
}) => {
  if (group.length > 1) {
    return (
      <div className="space-y-3 rounded-xl border border-primary/20 bg-primary/5 p-3">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-primary">
          <Link2 className="h-3.5 w-3.5" />
          Superset
        </div>
        {group.map((exercise) => (
          <ExercisePreview
            key={exercise.sortableId ?? exercise.id}
            exercise={exercise}
            compact={compact}
            getSetTargetLabel={getSetTargetLabel}
          />
        ))}
      </div>
    );
  }

  const exercise = group[0];
  if (!exercise) return null;
  return (
    <ExercisePreview
      exercise={exercise}
      compact={compact}
      getSetTargetLabel={getSetTargetLabel}
    />
  );
};

export const WorkoutImportPreview = ({
  workout,
  compact,
  getSetTargetLabel,
}: WorkoutImportPreviewProps) => {
  const blocks = groupSessionBlocks(
    workout.exerciseCollection,
    workout.sections,
  );

  if (workout.exerciseCollection.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        This template has no exercises yet.
      </p>
    );
  }

  return (
    <div className="space-y-5">
      {blocks.map((block, blockIndex) => {
        if (block.kind === "loose") {
          return (
            <div key={`loose-${blockIndex}`} className="space-y-4">
              {block.groups.map(({ group, groupIndex }) => (
                <GroupPreview
                  key={groupIndex}
                  group={group}
                  compact={compact}
                  getSetTargetLabel={getSetTargetLabel}
                />
              ))}
            </div>
          );
        }

        const Icon = isWorkoutSectionType(block.section.section_type)
          ? sectionTypeIcons[block.section.section_type]
          : sectionTypeIcons.standard;
        const typeLabel = isWorkoutSectionType(block.section.section_type)
          ? WORKOUT_SECTION_LABELS[block.section.section_type]
          : block.section.section_type;

        return (
          <div
            key={sessionBlockKey(block)}
            className="space-y-4 rounded-2xl border bg-muted/20 p-4"
          >
            <div className="flex flex-wrap items-center gap-2">
              <Icon className="h-4 w-4 text-muted-foreground" />
              <div className="text-sm font-semibold">
                {sessionSectionLabel(block.section)}
              </div>
              {hasCustomSectionName(block.section) ? (
                <div className="text-xs text-muted-foreground">{typeLabel}</div>
              ) : null}
              {block.section.rounds ? (
                <div className="text-xs text-muted-foreground">
                  {block.section.rounds} rounds
                </div>
              ) : null}
              {block.section.time_cap_seconds ? (
                <div className="text-xs text-muted-foreground">
                  Cap {Math.round(block.section.time_cap_seconds / 60)}m
                </div>
              ) : null}
            </div>
            {block.groups.map(({ group, groupIndex }) => (
              <GroupPreview
                key={groupIndex}
                group={group}
                compact={compact}
                getSetTargetLabel={getSetTargetLabel}
              />
            ))}
          </div>
        );
      })}
    </div>
  );
};

export const workoutImportSummary = (workout: Workout) => {
  const exerciseCount = workout.exerciseCollection.flat().length;
  const sectionCount = workout.sections.filter(
    (section) => !isLooseSection(section),
  ).length;
  const supersetCount = workout.exerciseCollection.filter(
    (group) => group.length > 1,
  ).length;
  return { exerciseCount, sectionCount, supersetCount };
};
